<!--
// app/pages/[region]/stats/TurnMvp.vue
-->
<script setup lang="ts">
import type { TurnMvpPlayer } from "~/src/player/domain/Player";
import type { GroupedRanking } from "~/src/stats/domain/models/Rankings";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { usePlayers } from "~/app/composables/players";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useSeasons } from "~/app/composables/season/seasons";
import { setAliasUrl } from "~/src/Shared/utils";
import statsService from "~/src/stats/domain/services/statsService";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import StatsDataTable from "~/app/components/pages/region-stats/StatsDataTable.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats/matchday-mvp",
    es: "/[region]/estadisticas/mvp-jornada",
    de: "/[region]/statistiken/mvp-spieltag",
    fr: "/[region]/statistiques/mvp-du-jour",
    it: "/[region]/statistiche/mvp-giornata",
    pt: "/[region]/estatisticas/mvp-rodada",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations", "not-found-nations", "not-found-world-cup"],
});

const { getSeasonTurnMvps } = usePlayers();
const { getSeasonParameterStats } = useSeasons();
const { currentSeason } = useCompetitionStore();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { mergeRankingsByPlayerId } = statsService();
const router = useRouter();
const localePath = useLocalePath();
const { isKings, isQueens, webContextGenders } = useWebContext();
const { t } = useI18n();
const tableStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const seasonMvpPartner = ref();
const loadingSeasonData = ref<boolean>(false);

const errorMessage = ref<boolean>(false);
const tableData = ref<GroupedRanking[] | undefined>([]);

const mainStat = ref<string>("MVP-T");
const otherStats = ref<string>("PG");
const allStatsPlayer: string[] = ["MVP-T", "PG"];
const fallbackMainStat: string = "MVP-T";

const dataSourceTableData = ref<TurnMvpPlayer[] | undefined>();

const getSeasonData = async (): Promise<void> => {
  if (
    !!seasonId.value &&
    $SEASONS_WITHOUT_DATA.includes(Number(seasonId.value)) &&
    Number(seasonId.value) !== 3
  ) {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;

      const seasonIdValue = seasonId.value;

      const { data } = await useFetch("/api/stats", {
        query: {
          season: seasonIdValue,
          type: "players",
          mainCode: mainStat.value,
          secondaryCodes: otherStats.value,
          orderBy: "ranking",
          sortOrder: "ASC",
          ctxGenders: webContextGenders,
        },
      });

      tableData.value = data.value;

      seasonMvpPartner.value = currentSeason ? currentSeason.metaInformation?.stats_partner : undefined;
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      tableStatsKey.value++;
      sidebarKey.value++;
    }
  } else if (!$SEASONS_WITHOUT_DATA.includes(Number(seasonId.value)) || !seasonId.value) {
    dataSourceTableData.value = seasonId.value ? await getSeasonTurnMvps(seasonId.value) : undefined;
    seasonMvpPartner.value = currentSeason ? currentSeason.metaInformation?.stats_partner : undefined;
  } else {
    await nextTick();
    await router.push(
      localePath({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  }
};

await getSeasonData();

useSeoMeta({
  title:
    t("header.navCompetition.stats") +
    " " +
    t("header.navCompetition.statsPlayers") +
    " - " +
    t("stats.sidebar.menu.TurnMvp"),
  ogTitle:
    t("header.navCompetition.stats") +
    " " +
    t("header.navCompetition.statsPlayers") +
    " - " +
    t("stats.sidebar.menu.TurnMvp"),
});

const { sortStatsTable } = statsService();

const executeSort = async sortValue => {
  const codeSortValue = sortValue.sortField.split(".")[1];
  mainStat.value = codeSortValue;
  tableData.value = await sortStatsTable(
    codeSortValue,
    allStatsPlayer,
    fallbackMainStat,
    seasonId.value,
    "players"
  );
};

const dataFieldsPlayers = computed(() => {
  return {
    ranking: `rankingData.${mainStat.value}.ranking`,
    name: "player",
    sortField: "rankingData.MVP-T.total",
    stats: [
      { stat: "rankingData.PG.total", header: t("PJ"), sortable: false, tooltip: t("tooltip.PJ") },
      { stat: "rankingData.MVP-T.total", header: t("MVP"), sortable: false, tooltip: t("tooltip.MVP-T") },
    ],
  };
});

const dataFieldsDataSourcePlayers = {
  ranking: "turnName",
  name: "player",
  sortField: undefined,
  stats: [{ stat: "", header: "", sortable: false, tooltip: "" }],
};
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('header.navCompetition.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div class="kql-w-container">
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
      />
    </div>
  </section>

  <section class="px-4 md:px-6 my-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0 relative">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3 relative">
        <div
          v-if="!!seasonMvpPartner && seasonId === Number(currentSeason?.id)"
          class="absolute -top-[7.2rem] lg:-top-7 right-0"
        >
          <NuxtImg
            :src="setAliasUrl(seasonMvpPartner.logo)"
            :alt="seasonMvpPartner.name"
            class="h-5 max-h-[21px]"
          />
        </div>
        <base-backdrop-loading :active="loadingSeasonData"></base-backdrop-loading>

        <div v-if="errorMessage">
          {{ $t("stats.message.notAvailable") }}
        </div>

        <div v-else class="stats-table-data max-w-full overflow-hidden" :key="tableStatsKey">
          <stats-data-table
            v-if="$SEASONS_WITHOUT_DATA.includes(Number(seasonId)) && tableData && tableData.length"
            :main-stat="mainStat"
            :table-data="tableData"
            :data-fields="dataFieldsPlayers"
            @sort="e => executeSort(e)"
          ></stats-data-table>
          <stats-data-table
            v-if="dataSourceTableData && !$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
            :main-stat="mainStat"
            :table-data="dataSourceTableData"
            :data-fields="dataFieldsDataSourcePlayers"
            is-turn-stat
          ></stats-data-table>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
